import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Receipt } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { GetBilloutLogByBilloutId } from 'src/services/BillOutService'
import moment from 'moment'
import { formatCurrency } from 'src/utils/Formatter'
import { inject } from 'mobx-react'
import { BilloutLogSearchRequest } from 'src/viewModels/BilloutLogSearchRequest'
import { BilloutLogDTO } from 'src/viewModels/BilloutLogDTO'
import { ClaimHeaderDetailsSummaryResult } from 'src/viewModels/ClaimHeaderDetailsSummaryResult'
import { IStores } from '../Stores'
import { IDismissClaimModal } from 'src/billOut/DismissClaimModal'
interface IBilloutLogModalProps {
  cms1500Id: string
  groupInfo: string
  patientHeader?: ClaimHeaderDetailsSummaryResult
  isOpen: boolean
  closeModal: () => void
}
class BilloutLogModal extends React.Component<IBilloutLogModalProps> {
  public componentDidMount(): void {
    if (this.props.isOpen) {
      this.setState({ cms1500Id: this.props.cms1500Id, page: 1, isLoading: true }, () => {
        this.getBilloutLogData()
      })
    }
  }

  public componentDidUpdate(prevProps: IBilloutLogModalProps): void {
    // Reload data when modal opens or when cms1500Id changes
    if (
      (!prevProps.isOpen && this.props.isOpen) ||
      (this.props.isOpen && prevProps.cms1500Id !== this.props.cms1500Id)
    ) {
      this.setState({ page: 1, cms1500Id: this.props.cms1500Id, isLoading: true }, () =>
        this.getBilloutLogData()
      )
    }
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private getBilloutLogData = async () => {
    const { page, pageSize } = this.state
    const cms1500Id = this.props.cms1500Id
    const data: BilloutLogSearchRequest = {
      cms1500Id,
      page,
      pageSize,
    }
    try {
      const response = await GetBilloutLogByBilloutId(data)
      this.setState(
        {
          gridData: response.items,
          pageCount: response.totalPages ?? 1,
          totalItems: response.totalItems ?? 0,
          isLoading: false,
        },
        () => {
          this.setPaginationOffsetData()
        }
      )
    } catch (_) {
      this.setState({
        gridData: [],
        pageCount: 0,
        totalItems: 0,
        firstRecordIndex: 0,
        lastRecordIndex: 0,
        isLoading: false,
      })
    }
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getBilloutLogData())
  }
  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }
  private mapLogGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={10} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell>{moment(record.createdDate).format('MM/DD/YYYY')}</TableCell>
          <TableCell>{record.createdByName}</TableCell>
          <TableCell align="right">
            {record.billoutTotal != undefined
              ? formatCurrency(record.billoutTotal.toString())
              : ''}
          </TableCell>
          <TableCell align="right">
            {record.totalAmountPaidToDate != undefined
              ? formatCurrency(record.totalAmountPaidToDate.toString())
              : ''}
          </TableCell>
        </TableRow>
      ))
    }
  }
  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }
  public state = {
    gridData: new pagedList().items as BilloutLogDTO[],
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 5,
    cms1500Id: '',
    isLoading: true,
  }
  public render() {
    return (
      <>
        <Dialog
          key={this.props.cms1500Id}
          fullWidth={true}
          maxWidth="md"
          open={this.props.isOpen!}
        >
          {this.state.isLoading ? (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: '2',
              }}
            >
              <CircularProgress size={100} />
            </div>
          ) : (
            <>
              <DialogTitle style={{ padding: 0 }}>
                <ResponsiveAppBar
                  title={
                    'Bill Out Log ' +
                    (this.props.patientHeader?.patientLast != undefined
                      ? '- ' +
                        this.props.patientHeader?.patientLast +
                        ', ' +
                        this.props.patientHeader?.patientFirst +
                        (this.props.patientHeader?.dateOfBirth
                          ? ' (' +
                            moment(this.props.patientHeader?.dateOfBirth).format(
                              'MM/DD/YYYY'
                            ) +
                            ')'
                          : '') +
                        ' ' +
                        this.props.groupInfo
                      : '')
                  }
                  pageIcon={<Receipt />}
                  isModal={true}
                />
              </DialogTitle>
              <DialogContent>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignContent="center"
                  alignItems="flex-start"
                  wrap="wrap"
                >
                  <Grid item xs={12} style={{ minWidth: '750px' }}>
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow className="gridPadding">
                          <TableCell>Created Date</TableCell>
                          <TableCell>Created By</TableCell>
                          <TableCell align="right">Bill Out Total</TableCell>
                          <TableCell align="right">Total Amount Paid to Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody style={{ width: '100%' }}>
                        {this.mapLogGridData()}
                      </TableBody>
                    </Table>
                    <div className="pagination-row">
                      {this.paginationInfo()}
                      <ReactPaginate
                        previousLabel={'<'}
                        nextLabel={'>'}
                        onPageChange={this.handlePageChange}
                        forcePage={this.state.page - 1}
                        pageCount={this.state.pageCount}
                        containerClassName={'pagination'}
                        activeClassName={'active'}
                        //@ts-ignore
                        renderOnZeroPageCount={null}
                      />
                    </div>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.props.closeModal} data-cy="close">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </>
    )
  }
}
const InjectedBilloutLogModal = inject<
  IStores,
  IBilloutLogModalProps,
  Partial<IDismissClaimModal>,
  any
>((stores: IStores) => ({
  close: stores.billOutStore.closeDialog,
  closeBillStatusDialog: stores.billOutStore.closeBillStatusDialog,
  cms1500: stores.billOutStore.selectedCMS1500,
  lineItemDetails: stores.billOutStore.selectedListItems,
  createOutgoingCMS1500: stores.billOutStore.createOutgoingCMS1500,
  isBillStatusDialogOpen: stores.billOutStore.isBillStatusDialogOpen,
  isCMS1500Saved: stores.billOutStore.isCMS1500Saved,
  isLoading: stores.billOutStore.isLoading,
  openBillOutNotesModal: stores.billOutStore.openBillOutNotesModal,
  openBillStatusDialog: stores.billOutStore.openBillStatusDialog,
  setIsCMS1500Saved: stores.billOutStore.setIsCMS1500Saved,
  patientHeader: stores.patients.headerDetails,
}))(BilloutLogModal)

export default InjectedBilloutLogModal
